/**
 * Google Search Console integration (Prompt Studio lot H).
 *
 * A brand owner connects a Google account via OAuth; the API stores the refresh token
 * KMS-encrypted on a `GSC_ACCOUNT#<googleSub>` tenants row (only the `gsc-reader` Lambda can
 * decrypt it) and maps the brand to a property on `GSC_CONN#<brandId>`. Query rows are fetched
 * on demand and NEVER persisted. See docs/plans/prompt-studio-build/H-search-console.md.
 */

/** Band state, computed only by `GET /brands/:brandId/search-console`. */
export type GscState = 'not_connected' | 'needs_property' | 'connected' | 'revoked' | 'property_access_lost';

export interface GscStatus {
  state: GscState;
  siteUrl?: string;
  connectedByEmail?: string;
  /** Only when `state = 'needs_property'`: every readable site, brand-matching ones first. */
  candidates?: { siteUrl: string; permissionLevel: string }[];
}

/** One Search Console query of the last 28 days. Proposals are built by the frontend via from-seeds (C12). */
export interface GscQueryRow {
  query: string;
  clicks: number;
  impressions: number;
}

export type GscImportResponse =
  | { state: 'ready'; rows: GscQueryRow[]; startDate: string; endDate: string; rowsTruncated: boolean }
  | { state: 'no_data' | 'revoked' | 'property_access_lost' | 'not_connected' | 'needs_property' };

export type GscCallbackResult = 'connected' | 'cancelled' | 'expired' | 'error' | 'scope_missing' | 'wrong_user';

/** Rows asked of Search Console per import (`rowLimit`). `rowsTruncated` = the reader returned exactly this many. */
export const GSC_ROW_LIMIT = 250;
/** Seeds per from-seeds call (C12 caps at 50). */
export const GSC_PROPOSE_MAX = 50;
